<template>
  <div class="fm-step-progress" role="list" :aria-label="`Step ${currentStep + 1} of ${steps.length}`">
    <div
      v-for="(step, idx) in steps"
      :key="step"
      role="listitem"
      :data-label="step"
      :aria-current="idx === currentStep ? 'step' : undefined"
      :class="{
        'fm-step': true,
        'fm-step--completed': idx < currentStep,
        'fm-step--active': idx === currentStep,
      }"
    >
      <div v-if="idx > 0" class="fm-step__connector"/>

      <div class="fm-step__bubble">
        <svg v-if="idx < currentStep" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <path d="M13.485 1.431a1.473 1.473 0 0 1 2.104 2.062l-7.84 9.801a1.473 1.473 0 0 1-2.12.04L.431 8.138a1.473 1.473 0 0 1 2.084-2.083l4.111 4.112 6.86-8.736z"/>
        </svg>
        <span v-else aria-hidden="true">{{ idx + 1 }}</span>
      </div>

      <span class="fm-step__label">{{ step }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StepProgress',
  props: {
    steps: {
      type: Array,
      default: () => [],
    },
    currentStep: {
      type: Number,
      default: 0,
    },
  },
};
</script>

<style lang="scss" scoped>
$amber:     #FFB000;
$ink:       #15171C;
$ink-soft:  #5B606B;
/* WCAG 1.4.3: was #9197A1 (~2.94:1 on white, fails 4.5:1 for the bubble
   number/label text of inactive steps). #6B7280 clears AA. */
$ink-faint: #6B7280;
$line:      #E7E4DA;

.fm-step-progress {
  display: flex;
  align-items: flex-start;
  width: 100%;
  padding: 0.25rem 0;
}

.fm-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  flex: 1;
  position: relative;
  min-width: 0;

  &__connector {
    position: absolute;
    top: 13px;
    right: 50%;
    left: -50%;
    height: 2px;
    background: $line;
    z-index: 0;
    transition: background 0.3s;
  }

  &--completed &__connector,
  &--active &__connector {
    background: $amber;
  }

  &__bubble {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    position: relative;
    z-index: 1;
    border: 2px solid $line;
    background: #fff;
    color: $ink-faint;
    flex-shrink: 0;
    transition: background 0.2s, border-color 0.2s, color 0.2s;

    svg { width: 9px; height: 9px; }
  }

  &--active &__bubble {
    border-color: $amber;
    background: $amber;
    color: $ink;
  }

  &--completed &__bubble {
    border-color: $amber;
    background: $amber;
    color: $ink;
  }

  &__label {
    font-size: 10.5px;
    font-weight: 500;
    color: $ink-faint;
    text-align: center;
    line-height: 1.25;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 80px;
    transition: color 0.2s, font-weight 0.2s;
  }

  &--active &__label { color: $ink; font-weight: 700; }
  &--completed &__label { color: $ink-soft; font-weight: 600; }
}

// ── Hover tooltip showing the full step name ──────────────────────────────────
.fm-step[data-label] {
  // Caret (points up toward the bubble)
  &::before {
    content: '';
    position: absolute;
    top: calc(100% + 2px);
    left: 50%;
    transform: translateX(-50%) scaleY(0.8);
    transform-origin: top center;
    border: 5px solid transparent;
    border-bottom-color: $ink;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.15s ease, transform 0.15s ease;
    z-index: 10;
  }

  // Tooltip label
  &::after {
    content: attr(data-label);
    position: absolute;
    top: calc(100% + 12px);
    left: 50%;
    transform: translateX(-50%) scale(0.92);
    transform-origin: top center;
    background: $ink;
    color: #fff;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
    padding: 6px 12px;
    border-radius: 8px;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.15s ease, transform 0.15s ease;
    box-shadow: 0 4px 14px rgba(21, 23, 28, 0.22);
    z-index: 10;
  }

  &:hover::before,
  &:hover::after {
    opacity: 1;
    transform: translateX(-50%) scale(1);
  }
}

@media (max-width: 576px) {
  .fm-step__label { display: none; }
  .fm-step__bubble { width: 22px; height: 22px; font-size: 10px; }
  .fm-step__connector { top: 10px; }
}
</style>
